import { Loader2, PlugZap } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "../../lib/api";
import { SelectField } from "../../components/SelectField";
import { Panel } from "./fields";
import { KeyField, SaveButton, useIntegrationForm } from "./integrationForm";
import { CardGatewaysPanel } from "./CardGatewaysPanel";

const KEYS = ["MPESA_ENV", "MPESA_CONSUMER_KEY", "MPESA_CONSUMER_SECRET", "MPESA_SHORTCODE", "MPESA_PASSKEY",
  "MPESA_PARTY_B", "MPESA_TRANSACTION_TYPE", "MPESA_CALLBACK_URL"];
const DEFAULT_CALLBACK = `${process.env.REACT_APP_BACKEND_URL}/api/mpesa/callback`;

export const PaymentsTab = () => {
  const { data, form, set, save, busy } = useIntegrationForm(KEYS);
  const [testing, setTesting] = useState(false);
  if (!form) return <Loader2 className="h-6 w-6 animate-spin text-brand" />;
  const test = async () => {
    setTesting(true);
    try {
      const { data: r } = await api.post("/admin/mpesa/test");
      toast.success(`Safaricom accepted the keys (${r.environment})`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setTesting(false);
    }
  };
  const live = data.mpesa_mode === "live";
  const p = { form, set };
  return (
    <div className="space-y-6" data-testid="admin-payments-tab">
      <div className={`rounded-2xl p-4 text-sm ${live ? "bg-brand-light text-ink" : "bg-gold/15 text-ink"}`} data-testid="mpesa-status">
        M-Pesa status: <b>{live ? "LIVE: guests get a real M-Pesa prompt" : "SIMULATED: no money moves until all keys below are saved"}</b>
      </div>
      <Panel title="M-Pesa (Safaricom Daraja)" hint="From your Daraja app at developer.safaricom.co.ke. Secret values stay hidden after saving; leave them as they are to keep them.">
        <div className="grid gap-5 md:grid-cols-2">
          <SelectField label="Environment" value={form.MPESA_ENV || "sandbox"} testid="mpesa-env-select" onChange={(v) => set({ MPESA_ENV: v })}
            options={[{ value: "sandbox", label: "Sandbox (testing)" }, { value: "production", label: "Production (real money)" }]} />
          <SelectField label="Account type" value={form.MPESA_TRANSACTION_TYPE || "CustomerPayBillOnline"} testid="mpesa-type-select"
            onChange={(v) => set({ MPESA_TRANSACTION_TYPE: v })}
            options={[{ value: "CustomerPayBillOnline", label: "Paybill" }, { value: "CustomerBuyGoodsOnline", label: "Till (Buy Goods)" }]} />
          <KeyField label="Consumer key" name="MPESA_CONSUMER_KEY" secret {...p} testid="mpesa-consumer-key" />
          <KeyField label="Consumer secret" name="MPESA_CONSUMER_SECRET" secret {...p} testid="mpesa-consumer-secret" />
          <KeyField label="Business shortcode" name="MPESA_SHORTCODE" {...p} testid="mpesa-shortcode" />
          <KeyField label="Passkey" name="MPESA_PASSKEY" secret {...p} testid="mpesa-passkey" />
          <KeyField label="Till number (Till accounts only)" name="MPESA_PARTY_B" hint="Leave blank for Paybill" {...p} testid="mpesa-party-b" />
          <KeyField label="Callback URL" name="MPESA_CALLBACK_URL" hint={DEFAULT_CALLBACK} {...p} testid="mpesa-callback-url" />
        </div>
        <p className="mt-3 text-xs text-ink/55">Callback URL: use this site's address followed by /api/mpesa/callback. A secret token is added automatically.</p>
        <div className="mt-5 flex flex-wrap gap-3">
          <SaveButton busy={busy} testid="mpesa-save" onClick={() => save({ MPESA_CALLBACK_URL: form.MPESA_CALLBACK_URL || DEFAULT_CALLBACK })} />
          <button type="button" className="btn-outline" onClick={test} disabled={testing} data-testid="mpesa-test">
            {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlugZap className="h-4 w-4" />}Test keys
          </button>
          {live && (
            <button type="button" className="btn-ghost !text-red-600" disabled={busy} data-testid="mpesa-remove-keys"
              onClick={() => window.confirm("Remove the M-Pesa keys? Payments go back to simulated mode.") && save({}, ["MPESA_CONSUMER_KEY", "MPESA_CONSUMER_SECRET", "MPESA_PASSKEY", "MPESA_SHORTCODE"])}>
              Remove keys
            </button>
          )}
        </div>
      </Panel>
      <CardGatewaysPanel />
    </div>
  );
};
